import React, { useRef, useState } from 'react';
import moment from 'moment-timezone';
import { ChevronDown, Star } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { fireStarBurst } from '../onboardingSupport/Celebrate';
import {
  useMarkScoreboardSeen,
  useScoreboard,
} from '../../../services/scoreboard';

// The header chip (Nic 2026-11-03, mockup option D): the viewer's own
// signups (Sales) or clients who reached Ready for CM (Onboarding), today,
// with NEW while there is one they have not opened the list for. Click:
// a plain list of who and when, the week's total under it; opening clears
// NEW on the server, so a refresh never brings it back. The only motion
// (Nic 2026-11-03: no pulse on the chip, it read as a purple ring shooting
// out; no full-screen confetti, no toast): a small burst of stars from the
// chip when the list opens on something new, and the unseen rows pop a
// star and settle.
const CT = 'America/Chicago';
const WORDS = {
  sales: { label: 'Signups', one: 'signup', eyebrow: 'Your signups' },
  // FOTC is the word the team knows (Nic 2026-11-03).
  onboarding: { label: 'FOTCs', one: 'FOTC', eyebrow: 'Your FOTCs' },
};

const ScoreboardChip = ({ team = 'sales', enabled = true }) => {
  const words = WORDS[team] || WORDS.sales;
  const q = useScoreboard(team, enabled);
  const seen = useMarkScoreboardSeen(team);
  const [open, setOpen] = useState(false);
  const mine = q.data?.mine || null;
  const unseen = mine?.unseen || 0;
  // Which rows were new when the list opened: they keep their star while it
  // is open, then read like the rest next time.
  const [fresh, setFresh] = useState(() => new Set());
  const chipRef = useRef(null);
  const onOpenChange = (next) => {
    setOpen(next);
    if (next) {
      setFresh(
        new Set((mine?.items || []).filter((i) => i.new).map((i) => i.id)),
      );
      if (unseen) {
        seen.mutate();
        // A small burst of stars from the chip (Nic 2026-11-03: the row's
        // star alone was too quiet).
        fireStarBurst(chipRef.current);
      }
    }
  };
  if (!enabled || !q.data) return null;
  // Today's rows sit above a short line, the rest of the week under it
  // (Nic 2026-11-04). The server's `today` is the Central date; the list is
  // newest first, so the line goes before the first row from another day.
  const todayYmd = q.data.today || moment.tz(CT).format('YYYY-MM-DD');
  const isToday = (it) =>
    moment.tz(it.at, CT).format('YYYY-MM-DD') === todayYmd;
  const firstPastIndex = mine.items.findIndex((it) => !isToday(it));
  const dividerAt = firstPastIndex > 0 ? firstPastIndex : -1;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={chipRef}
          type="button"
          aria-label={`${words.label}: ${mine.today} today, ${mine.week} this week${unseen ? `, ${unseen} new` : ''}`}
          className={cn(
            'relative inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] font-semibold outline-none transition-[background-color] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60',
            unseen
              ? 'border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100 dark:!border-violet-400/40 dark:!bg-violet-500/15 dark:text-violet-100'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:!border-white/10 dark:!bg-white/5 dark:text-slate-200 dark:hover:!bg-white/10',
          )}
        >
          <Star size={14} className="shrink-0 text-amber-500" />
          <span>{words.label}</span>
          <span className="tabular-nums">{mine.today}</span>
          <span className="font-medium text-slate-400">today</span>
          {unseen ? (
            <span className="rounded-full bg-violet-600 px-1.5 text-[10px] font-bold uppercase leading-[16px] tracking-wide text-white dark:!text-white">
              New
            </span>
          ) : null}
          <ChevronDown
            size={13}
            className={cn(
              'text-slate-400 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[300px] rounded-2xl p-3 shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-slate-200"
      >
        <div className="mb-1.5 flex items-center gap-1.5">
          <Star size={12} className="text-amber-500" />
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-violet-600 dark:text-violet-300">
            {words.eyebrow}
          </span>
          <span className="text-[11px] text-slate-400">· this week</span>
        </div>
        {mine.items.length ? (
          <ul className="mb-0 flex max-h-72 list-none flex-col gap-[2px] overflow-y-auto pl-0">
            {mine.items.map((it, index) => {
              const isNew = fresh.has(it.id);
              return (
                <React.Fragment key={it.id}>
                  {index === dividerAt ? (
                    <li
                      aria-hidden="true"
                      data-testid="scoreboard-day-divider"
                      className="mx-5 my-1 border-t border-slate-200 dark:border-white/15"
                    />
                  ) : null}
                  <li
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-2 py-1.5',
                      isNew ? 'animate-tint-fade' : '',
                    )}
                  >
                    <span className="w-4 shrink-0 text-center">
                      {isNew ? (
                        <Star
                          size={12}
                          className="inline-block animate-star-pop fill-amber-400 text-amber-500 motion-reduce:animate-none"
                          aria-label="New"
                        />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                      {it.name}
                    </span>
                    {/* The name is the point (Nic 2026-11-03): the day alone
                      beside it, nothing else. */}
                    <span
                      className="shrink-0 text-[11px] font-medium text-slate-400"
                      title={moment(it.at).format('ddd, MMM D [at] h:mm A')}
                    >
                      {moment(it.at).format('ddd')}
                    </span>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        ) : (
          <p className="mb-0 px-2 py-1.5 text-[12.5px] text-slate-400">
            No {words.one}s yet this week.
          </p>
        )}
        <div className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-400 dark:border-white/10">
          This week{' '}
          <b className="font-semibold tabular-nums text-slate-600 dark:text-slate-300">
            {mine.week}
          </b>{' '}
          · today{' '}
          <b className="font-semibold tabular-nums text-slate-600 dark:text-slate-300">
            {mine.today}
          </b>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ScoreboardChip;
